import React from 'react';
import { ListRenderItem, StyleSheet, View } from 'react-native';

import { UserAvatar } from '@components/Avatar';
import { ListItemPartialDivider } from '@components/Divider';
import { getExplorerItemKey } from '@components/Explorer/explore';
import type {
  ExplorerItem,
  ExplorerPlanItem,
  ExplorerUserItem,
} from '@components/Explorer/explore';
import FlatList from '@components/FlatList';
import Icon from '@components/Icon';
import { LargePlanCard } from '@components/Plan/Card';
import type { PlanCardNavigationCallbacks } from '@components/Plan/Card';
import { PressableOpacity } from '@components/Pressable';
import { Caption } from '@components/Typography';
import { isDatePast } from '@helpers/Dates';
import type { Plan, User } from '@types';

type ExplorerListProps = {
  /** Visible map/list items rendered in Explorer order. */
  items: ExplorerItem[];
  /** Returns loaded groups associated with a plan card. */
  getPlanGroups?: (plan: Plan) => React.ComponentProps<typeof LargePlanCard>['groupProfiles'];
  /** Empty state rendered when there are no visible rows. */
  ListEmptyComponent?: React.ComponentProps<typeof FlatList>['ListEmptyComponent'];
  /** Plan-card navigation callbacks owned by the parent screen. */
  planCardNavigation: PlanCardNavigationCallbacks;
  /** Called when the list is refreshed. */
  onRefresh?: () => void;
  /** Called when a resident row is pressed. */
  onUserPress?: (user: User) => void;
  /** Loaded users used to hydrate plan participant names and avatars. */
  participantUsers?: User[];
  /** Whether the list should show its refreshing state. */
  refreshing?: boolean;
  /** Style applied to the outer list. */
  style?: React.ComponentProps<typeof FlatList>['style'];
  /** Test id applied to the outer list. */
  testID?: string;
};

type PlanSectionId = 'upcomingPlans' | 'visitedPlans';

type ExplorerListRow =
  | {
      type: 'sectionHeader';
      id: PlanSectionId | 'residents';
      icon: React.ComponentProps<typeof Icon>['name'];
      title: string;
    }
  | {
      type: 'plan';
      id: string;
      item: ExplorerPlanItem;
      showDivider: boolean;
    }
  | {
      type: 'residents';
      id: 'residents';
      items: ExplorerUserItem[];
    };

const RESIDENT_AVATAR_SIZE = 50;

export default function ExplorerList({
  items,
  getPlanGroups = () => [],
  ListEmptyComponent,
  planCardNavigation,
  onRefresh,
  onUserPress,
  participantUsers = [],
  refreshing,
  style,
  testID,
}: ExplorerListProps) {
  const rows = React.useMemo(() => getExplorerListRows(items), [items]);

  const renderResidentItem: ListRenderItem<ExplorerUserItem> = ({ item }) => (
    <PressableOpacity
      accessibilityLabel={item.user.displayName}
      accessibilityRole="button"
      disabled={!onUserPress}
      onPress={() => onUserPress?.(item.user)}
      style={styles.residentItem}
      testID={`components.Explorer.List.Resident.${item.user.userId}`}>
      <UserAvatar
        displayName={item.user.displayName}
        photoUrl={item.user.photoUrl}
        size={RESIDENT_AVATAR_SIZE}
      />
      <Caption numberOfLines={1} style={styles.residentName}>
        {item.user.displayName}
      </Caption>
    </PressableOpacity>
  );

  const renderRow: ListRenderItem<ExplorerListRow> = ({ item }) => {
    if (item.type === 'sectionHeader') {
      return (
        <View style={styles.sectionHeader}>
          <Icon name={item.icon} size={15} style={styles.sectionIcon} />
          <Caption style={styles.sectionTitle}>{item.title}</Caption>
        </View>
      );
    }

    if (item.type === 'residents') {
      return (
        <FlatList
          horizontal
          data={item.items}
          renderItem={renderResidentItem}
          keyExtractor={getExplorerItemKey}
          contentContainerStyle={styles.residentListContent}
          style={styles.residentList}
          testID="components.Explorer.List.Residents"
        />
      );
    }

    return (
      <View>
        <LargePlanCard
          data={item.item.plan}
          groupProfiles={getPlanGroups(item.item.plan)}
          {...planCardNavigation}
          participantUsers={participantUsers}
        />
        {item.showDivider ? <ListItemPartialDivider /> : null}
      </View>
    );
  };

  return (
    <FlatList
      data={rows}
      renderItem={renderRow}
      keyExtractor={getExplorerListRowKey}
      onRefresh={onRefresh}
      refreshing={refreshing}
      ListEmptyComponent={ListEmptyComponent}
      style={style}
      testID={testID}
    />
  );
}

function getExplorerListRows(items: ExplorerItem[]): ExplorerListRow[] {
  const upcomingPlans: ExplorerPlanItem[] = [];
  const residents: ExplorerUserItem[] = [];
  const visitedPlans: ExplorerPlanItem[] = [];

  items.forEach((item) => {
    if (item.type === 'user') {
      residents.push(item);
      return;
    }

    if (isDatePast(item.plan.endDate)) {
      visitedPlans.push(item);
    } else {
      upcomingPlans.push(item);
    }
  });

  return [
    ...getPlanSectionRows('upcomingPlans', 'Upcoming', 'sign-direction', upcomingPlans),
    ...getResidentsSectionRows(residents),
    ...getPlanSectionRows('visitedPlans', 'Visited', 'pin-outline', visitedPlans),
  ];
}

function getPlanSectionRows(
  id: PlanSectionId,
  title: string,
  icon: React.ComponentProps<typeof Icon>['name'],
  items: ExplorerPlanItem[]
): ExplorerListRow[] {
  if (items.length === 0) return [];

  return [
    { type: 'sectionHeader', id, icon, title },
    ...items.map((item, index) => ({
      type: 'plan' as const,
      id: item.id,
      item,
      showDivider: index < items.length - 1,
    })),
  ];
}

function getResidentsSectionRows(items: ExplorerUserItem[]): ExplorerListRow[] {
  if (items.length === 0) return [];

  return [
    { type: 'sectionHeader', id: 'residents', icon: 'home-outline', title: 'Residents' },
    {
      type: 'residents',
      id: 'residents',
      items,
    },
  ];
}

function getExplorerListRowKey(item: ExplorerListRow) {
  return item.type === 'plan' ? item.id : `${item.type}:${item.id}`;
}

const styles = StyleSheet.create({
  residentItem: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingHorizontal: 6,
    width: 62,
  },
  residentList: {
    marginBottom: 8,
  },
  residentListContent: {
    paddingHorizontal: 10,
  },
  residentName: {
    letterSpacing: 0,
    lineHeight: 16,
    marginTop: 2,
    textAlign: 'center',
    width: '100%',
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: 6,
    marginHorizontal: 16,
    marginTop: 10,
  },
  sectionIcon: {
    flexShrink: 0,
    marginRight: 4,
  },
  sectionTitle: {
    fontWeight: '600',
    letterSpacing: 0,
    marginVertical: 0,
  },
});
